import { useState } from "react";
import { useTranslation } from "react-i18next";
import {usePageTitle} from "../helpers/page-title";
import Social from "../includes/social";

const RESET_DELAY = 3000; // ms

export default function Contact() {
    const { t } = useTranslation();

    usePageTitle(`${t('title.contact')} | ${t('title.gaocna')}`)

    const [backendMessage, setBackendMessage] = useState("");
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "",
        message: ""
    });

    const [submitState, setSubmitState] = useState("idle");
    // idle | loading | success | error

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const resetButton = () => {
        setTimeout(() => {
            setSubmitState("idle");
        }, RESET_DELAY);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setSubmitState("loading");
        setBackendMessage("");

        const dataToSend = { ...formData };
        setFormData({
            name: "",
            email: "",
            subject: "",
            message: ""
        });

        try {
            const response = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dataToSend)
            });

            const result = await response.json();

            if (!response.ok || result.status === "error") {
                setSubmitState("error");
                setBackendMessage(result.message || "An error occurred.");
            } else {
                setSubmitState("success");
                setBackendMessage(result.message || "Message sent successfully!");
            }
        } catch (error) {
            console.error(error);
            setSubmitState("error");
            setBackendMessage("Network error. Please try again.");
        } finally {
            resetButton();
        }
    };

    const getButtonText = () => {
        switch (submitState) {
            case "loading":
                return t("contact.form.sending");
            case "success":
                return t("contact.form.success");
            case "error":
                return t("contact.form.error");
            default:
                return t("contact.form.send");
        }
    };

    return (
        <>
        <Social />
        <main className="container" role="contact-page">
            {/* CONTACT FORM */}
            <section className="card message-form">
                <form onSubmit={handleSubmit} noValidate>
                    <h1>{t("contact.form.contact-us")}</h1>

                    <div className="form-fields">
                        <input
                            className="form-input"
                            name="name"
                            placeholder={t("contact.form.name")}
                            value={formData.name}
                            onChange={handleChange}
                            disabled={submitState === "loading"}
                            required
                        />

                        <input
                            className="form-input"
                            name="email"
                            type="email"
                            placeholder={t("contact.form.email")}
                            value={formData.email}
                            onChange={handleChange}
                            disabled={submitState === "loading"}
                            required
                        />

                        <input
                            className="form-input"
                            name="subject"
                            placeholder={t("contact.form.subject")}
                            value={formData.subject}
                            onChange={handleChange}
                            disabled={submitState === "loading"}
                        />

                        <textarea
                            className="form-input"
                            name="message"
                            placeholder={t("contact.form.message-placeholder")}
                            value={formData.message}
                            onChange={handleChange}
                            disabled={submitState === "loading"}
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        className={`${submitState}`}
                        disabled={submitState !== "idle"}
                    >
                        {getButtonText()}
                    </button>
                </form>
            </section>

            {/* CONTACT DETAILS */}
            <aside className="card contact-details">
                {/* CHURCH PERSONNEL */}
                <section className="church-personnel">
                    <ul className="contact-list">
                        <li className="contact-item">
                            {t("contact.personnel.diocese-leader")}
                        </li>
                        <li className="contact-item">
                            {t("contact.personnel.bishop-saba")}
                        </li>
                        <li className="contact-item">
                            <strong>{t("contact.web.phone")}:</strong> +1 (267) 777-0971
                        </li>
                        <li className="contact-item">
                            <strong>{t("contact.web.email")}:</strong> hierarch@gaocna.org
                        </li>
                    </ul>

                    <ul className="contact-list">
                        <li className="contact-item">
                            {t("contact.personnel.diocese-secretary")}
                        </li>
                        <li className="contact-item">
                            {t("contact.personnel.monk-jerome")}
                        </li>
                        <li className="contact-item">
                            <strong>{t("contact.web.phone")}:</strong> +1 (215) 800-2635
                        </li>
                        <li className="contact-item">
                            <strong>{t("contact.web.email")}:</strong> example@example.com
                        </li>
                    </ul>
                </section>

                {/* WEBSITE CONTACT */}
                <section className="website-maintenance">
                    <ul className="contact-list">
                        <li className="contact-item">
                            {t("contact.web.website-contact")}
                        </li>
                        <li className="contact-item">
                            <strong>{t("contact.web.developer")}:</strong> {t("contact.web.person-name")}
                        </li>
                        <li className="contact-item">
                            <strong>{t("contact.web.email")}:</strong> andria24b@gmail.com
                        </li>
                        <li className="contact-item">
                            <strong>{t("contact.web.phone")}:</strong> +1 (267) 632-6754
                        </li>
                    </ul>
                </section>

                {/* MAP */}
                <section className="location-map">
                    <h2 className="contact-title">{t("contact.map.our-location")}</h2>
                    <div className="map-wrapper">
                        <iframe
                            title="Office location map"
                            src="https://www.google.com/maps?q=412+Philmont+Ave,+Feasterville,+PA+19053&output=embed"
                            loading="eager"
                            height="100%"
                            width="100%"
                            style={{ border: "none" }}
                        />
                    </div>
                </section>
            </aside>
        </main>
        </>
    );
}
